import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import {
  Spacer,
  PageDS,
  ToastContainerDS,
  TabMenu,
  SelectDS,
  TextCapitalized,
} from "@qivia/ui";
import { useNavigate, useParams } from "react-router-dom";
import "../../i18n/i18n";
import { CardsTab } from "./cardsTab";
import { ShipmentsTab } from "./shipmentsTab";
import { UnblockPinOfflineTab } from "./unblockPinOfflineTab";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  selectCompaniesListStatus,
  companiesListAsync,
  selectCompaniesList,
} from "../companiesSlice";
import { cardsListAsync, shipmentsListAsync } from "./cardsSlice";
import styled from "styled-components";
import { selectAdminUser } from "../homeSlice";

export const Cards = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useAppDispatch();
  const companiesList = useAppSelector(selectCompaniesList);
  const companiesListStatus = useAppSelector(selectCompaniesListStatus);
  const [selectedTab, setSelectedTab] = useState<string>(params.tab ?? "cards");
  const [companySelected, setCompanySelected] = useState<string>("");
  const adminUser = useAppSelector(selectAdminUser);
  const optionCompany = useMemo(
    () =>
      companiesList.map((company) => {
        return {
          label: company.name,
          value: company.uuid,
        };
      }),
    [companiesList],
  );

  const valueOptionCompany = useMemo(
    () => optionCompany.find((option) => option.value === companySelected),
    [companySelected, optionCompany],
  );

  useEffect(() => {
    if (selectedTab) {
      navigate(
        `/home/cards/${selectedTab}${
          params.id !== undefined ? `/${params.id}` : ""
        }`,
      );
    }
  }, [navigate, params.id, selectedTab]);

  useEffect(() => {
    if (companiesListStatus === "idle") {
      void dispatch(companiesListAsync());
    }
  }, [companiesListStatus, dispatch]);

  useEffect(() => {
    if (params.id && !companySelected) {
      setCompanySelected(params.id);
    }
  }, [companySelected, params.id]);

  useEffect(() => {
    if (companySelected) {
      void dispatch(cardsListAsync({ companyUuid: companySelected }));
      void dispatch(shipmentsListAsync());
      navigate(`/home/cards/${params.tab}/${companySelected}`);
    }
  }, [companySelected, dispatch, navigate, params.tab]);

  const tabs = useMemo(() => {
    const baseTabs = [
      {
        name: "cards",
        title: t("cards_shipments.tab.cards"),
      },
      {
        name: "shipments",
        title: t("cards_shipments.tab.shipments"),
      },
    ];

    if (adminUser?.email !== "exterieur1@qivia.fr") {
      baseTabs.push({
        name: "unblockPinOffline",
        title: t("cards_shipments.pin.tab.unblockPinOffline"),
      });
    }

    return baseTabs;
  }, [adminUser?.email, t]);

  const pageTitle = useMemo(
    () => tabs.find((tab) => tab.name === selectedTab)?.title ?? "",
    [selectedTab, tabs],
  );

  return (
    <PageDS
      title={pageTitle}
      toaster={<ToastContainerDS />}
      topElement={
        <>
          <StyledHeader>
            <TextCapitalized>
              {t("cards_shipments.select.company")}
            </TextCapitalized>
            <SelectDS
              label={""}
              value={valueOptionCompany}
              options={optionCompany}
              onChange={(selectedOption) =>
                setCompanySelected(selectedOption ? selectedOption.value : "")
              }
              allWidth
            />
          </StyledHeader>
          <Spacer y={1} />
          <TabMenu
            tabs={tabs}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
          />
        </>
      }
    >
      <CardsTab />
      <ShipmentsTab />
      <UnblockPinOfflineTab />
    </PageDS>
  );
};

const StyledHeader = styled.div`
  display: flex;
  flex-direction: column;
  width: 30rem;
  z-index: 10;
`;
